/**
 * Canonical enum normalizer.
 *
 * Accepts BOTH v1 and v2 enum values and normalizes to canonical v2 form.
 * v2 values pass through unchanged. v1 values are mapped to their v2 equivalents.
 *
 * This handles backward compatibility: existing saved workflows may contain
 * either v1 or v2 values. After normalization, the frontend always works
 * with v2 values internally.
 */

const TO_CANONICAL = {
  document_editor: {
    sourceType: {
      EDITOR: 'editor', DOCX: 'docx', URL: 'url',
      editor: 'editor', docx: 'docx', url: 'url',
    },
    exportFormat: {
      DOCX: 'docx', PDF: 'pdf',
      docx: 'docx', pdf: 'pdf',
    },
  },
  merge_workflow: {
    mode: {
      ALL: 'all', ANY: 'any', OROFAND: 'or_and',
      all: 'all', any: 'any', or_and: 'or_and',
    },
  },
  time_delay: {
    mode: {
      TIME_ELAPSED: 'elapsed', CERTAIN_DATE_TIME: 'specific_datetime',
      CERTAIN_WEEK_DAY: 'day_of_week', AT_DATE_TIME_PLACEHOLDER: 'placeholder_datetime',
      elapsed: 'elapsed', specific_datetime: 'specific_datetime',
      day_of_week: 'day_of_week', placeholder_datetime: 'placeholder_datetime',
    },
    dayOfWeek: {
      Mon: 'MON', Tue: 'TUE', Wed: 'WED', Thu: 'THU', Fri: 'FRI', Sat: 'SAT', Sun: 'SUN',
      MON: 'MON', TUE: 'TUE', WED: 'WED', THU: 'THU', FRI: 'FRI', SAT: 'SAT', SUN: 'SUN',
    },
  },
  trigger_on_database_update: {
    triggerType: {
      RECORD_CREATED: 'record_created', ANY_UPDATE: 'any_update', SPECIFIC_COLUMN: 'specific_column',
      TRIGGER_TYPE_RECORD_CREATED: 'record_created', TRIGGER_TYPE_ANY_UPDATE: 'any_update', TRIGGER_TYPE_SPECIFIC_COLUMN: 'specific_column',
      record_created: 'record_created', any_update: 'any_update', specific_column: 'specific_column',
    },
  },
  conditional_workflow: {
    function: {
      EQUAL: 'equal', NOTEQUAL: 'not_equal',
      CONTAIN: 'contains', NOTCONTAIN: 'not_contains',
      HAS_SELECTED: 'has_selected', HAS_NOT_SELECTED: 'has_not_selected',
      MATCHES: 'matches', NOTMATCH: 'not_matches',
      GREATERTHAN: 'greater_than', GREATERTHANOREQUAL: 'greater_than_or_equal',
      LESSTHAN: 'less_than', LESSTHANOREQUAL: 'less_than_or_equal',
      NOTEMPTY: 'not_empty', EMPTY: 'empty',
      equal: 'equal', not_equal: 'not_equal',
      contains: 'contains', not_contains: 'not_contains',
      has_selected: 'has_selected', has_not_selected: 'has_not_selected',
      matches: 'matches', not_matches: 'not_matches',
      greater_than: 'greater_than', greater_than_or_equal: 'greater_than_or_equal',
      less_than: 'less_than', less_than_or_equal: 'less_than_or_equal',
      not_empty: 'not_empty', empty: 'empty',
    },
    logic: { AND: 'and', OR: 'or', and: 'and', or: 'or' },
    result: { CONTINUE: 'continue', STOP: 'stop', continue: 'continue', stop: 'stop' },
    defaultResult: { CONTINUE: 'continue', STOP: 'stop', continue: 'continue', stop: 'stop' },
  },
  schedule_start: {
    'stopCondition.type': {
      after_a_certain_number_of_repeats: 'after_count', once_a_date_time_is_reached: 'on_date',
      after_count: 'after_count', on_date: 'on_date',
    },
    'repeat.unit': {
      businessDays: 'businessDay', businessDay: 'businessDay',
    },
  },
  code: {
    language: {
      Python_3_9: 'python_3_9', NodeJS_16: 'nodejs_16',
      python_3_9: 'python_3_9', nodejs_16: 'nodejs_16', nodejs_18: 'nodejs_16',
    },
  },
  trigger_from_api_polling: {
    responseFormat: {
      JSON: 'json', XML: 'xml', TEXT: 'text',
      json: 'json', xml: 'xml', text: 'text',
    },
    'pollingInterval.unit': {
      minutes: 'minute', hours: 'hour', days: 'day',
      minute: 'minute', hour: 'hour', day: 'day',
    },
  },
  await_external_event: {
    pollingResponseFormat: {
      JSON: 'json', XML: 'xml', TEXT: 'text',
      json: 'json', xml: 'xml', text: 'text',
    },
    'pollingInterval.type': {
      minutes: 'minute', hours: 'hour', days: 'day',
      minute: 'minute', hour: 'hour', day: 'day',
    },
    'timeout.type': {
      minutes: 'minute', hours: 'hour', days: 'day',
      minute: 'minute', hour: 'hour', day: 'day',
    },
  },
};

function applyFlat(data, fieldName, valueMap) {
  if (fieldName in data) {
    const cur = data[fieldName];
    if (typeof cur === 'string' && cur in valueMap) {
      data[fieldName] = valueMap[cur];
    }
  }
}

function applyDotPath(data, dotPath, valueMap) {
  const parts = dotPath.split('.');
  let obj = data;
  for (let i = 0; i < parts.length - 1; i++) {
    if (obj == null || typeof obj !== 'object') return;
    obj = obj[parts[i]];
  }
  const leaf = parts[parts.length - 1];
  if (obj != null && typeof obj === 'object' && leaf in obj) {
    const cur = obj[leaf];
    if (typeof cur === 'string' && cur in valueMap) {
      obj[leaf] = valueMap[cur];
    }
  }
}

/**
 * Recursively walk arrays/objects and apply field-level mappings wherever
 * a mapped field name appears. Handles the deeply nested conditional_workflow
 * rule structure (arrays of objects containing `function`, `logic`, etc.).
 */
function applyDeep(obj, fieldMappings) {
  if (obj == null || typeof obj !== 'object') return;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      applyDeep(item, fieldMappings);
    }
    return;
  }

  for (const [fieldName, valueMap] of Object.entries(fieldMappings)) {
    if (fieldName.includes('.')) continue;
    if (fieldName in obj) {
      const cur = obj[fieldName];
      if (typeof cur === 'string' && cur in valueMap) {
        obj[fieldName] = valueMap[cur];
      }
    }
  }

  for (const val of Object.values(obj)) {
    if (val != null && typeof val === 'object') {
      applyDeep(val, fieldMappings);
    }
  }
}

/**
 * Normalize enum values to canonical v2 form.
 *
 * Accepts both v1 and v2 inputs, always outputs canonical v2 values.
 *
 * @param {string} componentType - e.g. 'document_editor', 'merge_workflow'
 * @param {object} rawData - the extracted component data (will be mutated)
 * @returns {object} the same rawData object, with mapped values replaced
 */
export function normalizeToCanonical(componentType, rawData) {
  if (!rawData || typeof rawData !== 'object') return rawData;

  const fieldMappings = TO_CANONICAL[componentType];
  if (!fieldMappings) return rawData;

  const hasDeepFields = componentType === 'conditional_workflow';

  for (const [fieldName, valueMap] of Object.entries(fieldMappings)) {
    if (fieldName.includes('.')) {
      applyDotPath(rawData, fieldName, valueMap);
    } else if (!hasDeepFields) {
      applyFlat(rawData, fieldName, valueMap);
    }
  }

  if (hasDeepFields) {
    applyDeep(rawData, fieldMappings);
  }

  return rawData;
}
